import { useEffect, useState } from 'react';
import { useInputHelper } from 'framework/form';
import { getWithThousandsSeparator, convertCurrency, useDidUpdateEffect } from 'framework/utils';
import { get, isEqual } from 'lodash';
import { BaseInputErrors } from 'framework/ui/layout';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

const transferListTotalSx: SxProps<Theme> = theme => ({
  fontWeight: 600,

  [theme.breakpoints.up('smallMobile')]: {
    display: 'flex',
    position: 'absolute',
    bottom: '65px',
    right: 0,
    left: 0,
    marginRight: '46px',
    marginLeft: '-16px',

    '& > *': {
      width: '25%',
      paddingLeft: '16px',
      position: 'relative',

      '&:last-child': {
        marginRight: 0,
      },

      '&.form-error': {
        paddingLeft: '10px',
      },
    },

    '& .journal-entry-total-label': {
      display: 'none',
    },

    '& .form-error': {
      position: 'absolute',
      bottom: '-13px',
      left: 'calc(25% + 5px)',
      width: '75%',
    },
  },

  [theme.breakpoints.down('smallMobile')]: {
    marginBottom: '20px',

    '& .journal-entry-total-title': {
      display: 'none',
    },
  },

  '& .form-error': {
    display: 'block',
    fontSize: '11px',
    lineHeight: 1.2,
    fontWeight: 400,
    color: 'error.main',
  },
});

const getTotal = (data: any[], source: string, exchangeRates: any = {}) =>
  data.reduce((acc: number, item: any) => {
    const { currency } = item;
    const value = get(item, source) || 0;
    const exchangeRate = exchangeRates?.[currency]?.USD || 1;

    return +acc + +(currency && currency !== 'USD' ? convertCurrency(value, exchangeRate) : +value);
  }, 0);

const TransferListInputTotal = ({ transfersSource, exchangeRatesSource }: any) => {
  const { getValue: getTransfers } = useInputHelper({
    source: transfersSource,
  });
  const { getValue: getExchangeRates } = useInputHelper({
    source: exchangeRatesSource,
  });
  const { getValue, getSource, getRawOnChangeCallback, getErrorMessages, getValidationCallback } = useInputHelper({
    source: 'totals',
  });
  const transfers = (getTransfers() as any) || [];
  const exchangeRates = getExchangeRates() as any;
  const value = getValue() as any;
  const rawOnChangeCallback = getRawOnChangeCallback();
  const validationCallback = getValidationCallback();
  const source = getSource();
  const [lastUsedExchangeRates, setLastUsedExchangeRates] = useState(exchangeRates);
  const debitTotal = getTotal(transfers, 'debit', lastUsedExchangeRates);
  const creditTotal = getTotal(transfers, 'credit', lastUsedExchangeRates);
  const errors = getErrorMessages();

  useEffect(() => {
    if (rawOnChangeCallback && exchangeRates?.date) {
      rawOnChangeCallback({
        [source]: debitTotal !== creditTotal ? null : debitTotal,
      });
    }
    // Suppressed warnings because we only need to call useEffect callback after updating debitTotal and creditTotal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debitTotal, creditTotal]);

  useDidUpdateEffect(() => {
    if (validationCallback) {
      validationCallback();
    }
  }, [value]);

  useEffect(() => {
    if (!isEqual(lastUsedExchangeRates, exchangeRates)) {
      setLastUsedExchangeRates(exchangeRates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfers]);

  return (
    <Box sx={transferListTotalSx}>
      <Typography variant="subtitle1" component="span" className="journal-entry-total-title">
        Total:
      </Typography>
      <div>
        <Typography variant="subtitle1" component="span" className="journal-entry-total-label">
          Total Debit:{' '}
        </Typography>
        {getWithThousandsSeparator(debitTotal || 0)}$
      </div>
      <div>
        <Typography variant="subtitle1" component="span" className="journal-entry-total-label">
          Total Credit:{' '}
        </Typography>
        {getWithThousandsSeparator(creditTotal || 0)}$
      </div>
      <BaseInputErrors errorMessages={errors} />
    </Box>
  );
};

export default TransferListInputTotal;
